import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Input, Stack, Typography } from '@mui/joy';
import { DotsSixVertical } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AttributeColorSwatch } from 'companySettings/components/PartFinderAttributeValues';
import { Button, SlidingPanel, SlidingPanelContent } from 'core/components';
import { enterPress } from 'core/utils';
import { Color, Direction, Level, Variant } from 'theme/enums';

import { moveItem } from './utils';

export interface IOrderItem {
  colorHex?: string;
  id: string;
  label: string;
}

interface IRowProps {
  item: IOrderItem;
  position: number;
  onChangePosition: (position: number) => void;
}

// One item: drag handle, its position in an editable box, then the item itself, read-only.
const OrderRow = ({ item, position, onChangePosition }: IRowProps) => {
  const [draftPosition, setDraftPosition] = useState(String(position));

  const { t } = useTranslation();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const commitPosition = () => {
    const requestedPosition = Number(draftPosition);

    if (!Number.isInteger(requestedPosition) || requestedPosition < 1) {
      setDraftPosition(String(position));

      return;
    }

    // A position clamped back to this row's own leaves the row where it is, so the box shows it
    // again; a real move updates it through the position.
    setDraftPosition(String(position));
    onChangePosition(requestedPosition);
  };

  // A drag, or a position typed into another row, moves this row too.
  useEffect(() => {
    setDraftPosition(String(position));
  }, [position]);

  return (
    <Stack
      ref={setNodeRef}
      direction={Direction.ROW}
      sx={{
        alignItems: 'center',
        gap: 1.25,
        px: 1.5,
        py: 1,
        borderRadius: 6,
        backgroundColor: 'neutral.50',
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{ display: 'flex', color: 'neutral.500', cursor: 'grab', touchAction: 'none' }}
      >
        <DotsSixVertical weight="bold" />
      </Box>

      <Input
        value={draftPosition}
        slotProps={{
          input: {
            'aria-label': t('labelOrder'),
            inputMode: 'numeric',
            sx: { textAlign: 'center' },
          },
        }}
        onChange={(event) => setDraftPosition(event.target.value)}
        onBlur={commitPosition}
        onKeyDown={(event) => enterPress(event, commitPosition)}
        sx={{ width: 38, minWidth: 38, px: 0, flexShrink: 0 }}
      />

      <Stack
        direction={Direction.ROW}
        sx={{
          alignItems: 'center',
          gap: 1,
          flexGrow: 1,
          px: 2,
          py: 1,
          borderRadius: 6,
          backgroundColor: 'neutral.100',
          overflow: 'hidden',
        }}
      >
        {item.colorHex && <AttributeColorSwatch colorHex={item.colorHex} />}
        <Typography level={Level.TITLE_SM} sx={{ color: 'neutral.700', overflow: 'hidden' }}>
          {item.label}
        </Typography>
      </Stack>
    </Stack>
  );
};

interface IProps {
  items: IOrderItem[];
  title: string;
  subtitle: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (orderedIds: string[]) => void;
  level?: number;
}

// Reorders a list of items by drag or by typing a position. Nothing is persisted here: the new
// order goes back to the form the panel was opened from.
export const OrderPanel = ({
  items,
  title,
  subtitle,
  isOpen,
  onClose,
  onSave,
  level = 1,
}: IProps) => {
  const [orderedItems, setOrderedItems] = useState(items);

  const { t } = useTranslation();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const isOrderChanged = orderedItems.some((item, index) => item.id !== items[index]?.id);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    setOrderedItems((current) =>
      moveItem(
        current,
        current.findIndex((item) => item.id === active.id),
        current.findIndex((item) => item.id === over.id)
      )
    );
  };

  // A typed position is 1-based and can be anything the user types, so it is clamped to the list.
  const onChangePosition = (index: number, requestedPosition: number) => {
    const targetIndex = Math.min(Math.max(requestedPosition - 1, 0), orderedItems.length - 1);

    if (targetIndex === index) {
      return;
    }

    setOrderedItems((current) => moveItem(current, index, targetIndex));
  };

  const onSaveOrder = () => {
    onSave(orderedItems.map((item) => item.id));
    onClose();
  };

  const onDiscard = () => {
    setOrderedItems(items);
    onClose();
  };

  // The items are rebuilt on every render of the editor, so the order starts over only when the
  // panel opens, never while the user is changing it.
  useEffect(() => {
    if (isOpen) {
      setOrderedItems(items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={onDiscard}
      title={title}
      level={level}
      buttons={
        <Stack direction={Direction.ROW} sx={{ gap: 2 }}>
          <Button
            text={t('buttonLabelSaveChanges')}
            disabled={!isOrderChanged}
            onClick={onSaveOrder}
          />
          <Button
            text={t('buttonLabelDiscardChanges')}
            variant={Variant.SOFT}
            color={Color.NEUTRAL}
            onClick={onDiscard}
          />
        </Stack>
      }
    >
      {isOpen && (
        <SlidingPanelContent fullWidth={false}>
          <Stack sx={{ gap: 1.5 }}>
            <Typography level={Level.TITLE_MD}>{subtitle}</Typography>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext
                items={orderedItems.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <Stack sx={{ gap: 1.5 }}>
                  {orderedItems.map((item, index) => (
                    <OrderRow
                      key={item.id}
                      item={item}
                      position={index + 1}
                      onChangePosition={(position) => onChangePosition(index, position)}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
          </Stack>
        </SlidingPanelContent>
      )}
    </SlidingPanel>
  );
};
